import type {
  ChannelLimitData,
  ChannelLimitSegment,
} from '@lmt/signal-viewer-ui';
import type { TherapySettingParameter } from '../api/therapy-archive-api';
import type { ResolvedSection } from './therapy-archive';

type LimitLocalizerKeyConfig = {
  /** Slot key matching a channel template's `ChannelLimitConfig.type`. */
  type: string;
  maxLocalizerKeys: string[];
  minLocalizerKeys: string[];
};

/**
 * Full localizer keys per limit slot. `localizerKey` is the only stable handle:
 * the plugin sets `ParameterId = -1` and the backend localizes the display name.
 * Keys vary by mode and generation (APAP `ApapMax…`, AutoST `EPAPMax…`; P2.8 and
 * P3 differ in path prefix), so each variant is matched in full —
 * `EPAPMax_Long_Dotted` is a substring of the distinct `EEPAPMax_Long_Dotted`.
 */
const LIMIT_LOCALIZER_KEY_CONFIGS: LimitLocalizerKeyConfig[] = [
  {
    type: 'pressure-limits',
    maxLocalizerKeys: [
      'Parameters/P28/Adjust/ApapMax_Short_Dotted',
      'Parameters/Adjust/ParameterNames/ApapMax_Short_Dotted',
      'Parameters/Adjust/ParameterNames/EPAPMax_Long_Dotted',
    ],
    minLocalizerKeys: [
      'Parameters/P28/Adjust/ApapMin_Short_Dotted',
      'Parameters/Adjust/ParameterNames/ApapMin_Short_Dotted',
      'Parameters/Adjust/ParameterNames/EPAPMin_Long_Dotted',
    ],
  },
];

function findNumericValue(
  parameters: TherapySettingParameter[],
  localizerKeys: string[],
): number | null {
  const parameter = parameters.find(
    (p) =>
      p.isVisible &&
      p.localizerKey != null &&
      localizerKeys.includes(p.localizerKey),
  );
  if (!parameter) return null;

  const value = Number(parameter.displayValue);
  return Number.isFinite(value) ? value : null;
}

function getSectionSegments(
  section: ResolvedSection,
  config: LimitLocalizerKeyConfig,
): ChannelLimitSegment[] {
  const sorted = [...section.snapshots].sort(
    (left, right) => left.startMs - right.startMs,
  );

  return sorted
    .map((snapshot, index): ChannelLimitSegment | null => {
      const next = sorted[index + 1];
      const startMs = snapshot.startMs;
      const endMs = Math.min(
        next ? next.startMs : section.endMs,
        section.endMs,
      );
      if (startMs >= endMs) return null;

      const max = findNumericValue(snapshot.parameters, config.maxLocalizerKeys);
      const min = findNumericValue(snapshot.parameters, config.minLocalizerKeys);
      // Doubles as mode gating: the params only surface in modes with limits.
      if (max == null || min == null) return null;

      return { startMs, endMs, max, min };
    })
    .filter((segment): segment is ChannelLimitSegment => segment != null);
}

function deriveLimitSegments(
  sections: ResolvedSection[],
  config: LimitLocalizerKeyConfig,
): ChannelLimitSegment[] {
  return sections
    .flatMap((section) => getSectionSegments(section, config))
    .sort((left, right) => left.startMs - right.startMs);
}

/**
 * Reconstructs the recorded alarm-limit steps for every limit slot from the
 * resolved therapy-settings wire sections. Segments are sorted by `startMs`; a
 * slot's segments are empty when its limit params are absent (e.g. P3.2/P5
 * recordings, or fixed-pressure modes).
 */
export function extractChannelLimits(
  sections: ResolvedSection[],
): ChannelLimitData[] {
  return LIMIT_LOCALIZER_KEY_CONFIGS.map((config) => ({
    type: config.type,
    segments: deriveLimitSegments(sections, config),
  }));
}
